/**
 * Centralized navigation helpers
 * 
 * These functions ensure all redirects are relative paths (not hardcoded domains)
 * and work correctly across all environments (Vercel preview, production)
 */

/**
 * Normalizes a path to ensure it starts with /
 * @param path - Path to normalize (e.g., "app" or "/app")
 * @returns Normalized path starting with /
 */
export function toAppPath(path: string = "/app"): string {
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Performs a robust redirect using window.location.assign
 * This works across all environments and doesn't depend on hardcoded domains
 * @param path - Relative path to redirect to (e.g., "/app" or "app")
 */
export function redirectTo(path: string): void {
  const safePath = toAppPath(path);
  window.location.assign(safePath);
}

/**
 * Returns an absolute URL for a given path
 * Useful for OAuth redirects or external links that require absolute URLs
 * @param path - Relative path (e.g., "/app" or "/auth/callback")
 * @returns Absolute URL using current origin (e.g., "https://yourdomain.com/app")
 */
export function absoluteUrl(path: string): string {
  const safePath = toAppPath(path);
  if (typeof window === 'undefined') {
    // SSR fallback (shouldn't happen in SPA, but defensive)
    return safePath;
  }
  return `${window.location.origin}${safePath}`;
}

